/**
 * apiClient.js
 * HTTP API Client for React UI Layer.
 */

import { captureException } from './analytics';

export const API_BASE_URL = import.meta.env.VITE_API_URL || 
  (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1' 
    ? 'https://dhudd-backend.onrender.com/api' 
    : 'http://localhost:5000/api');

export function getAvatarUrl(url) {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }
  let origin = API_BASE_URL.startsWith('http')
    ? API_BASE_URL.replace(/\/api\/?$/, '').replace(/\/$/, '')
    : (typeof window !== 'undefined' ? window.location.origin : 'https://dhudd-backend.onrender.com');
  return `${origin}${url.startsWith('/') ? '' : '/'}${url}`;
}

export function getToken() {
  return localStorage.getItem('tezer_token') || localStorage.getItem('dhudd_token');
}

export function setToken(token) {
  if (token) {
    localStorage.setItem('tezer_token', token);
    localStorage.setItem('dhudd_token', token);
  } else {
    localStorage.removeItem('tezer_token');
    localStorage.removeItem('dhudd_token');
  }
}

export function logout() {
  if (typeof localStorage !== 'undefined') {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('dhudd_') || key.startsWith('tezer_'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
  }
}

export function isOnline() {
  return typeof navigator !== 'undefined' && navigator.onLine;
}

export async function request(endpoint, method = 'GET', body = null) {
  let finalMethod = 'GET';
  let finalBody = null;
  let customHeaders = {};

  if (typeof method === 'object' && method !== null) {
    finalMethod = method.method || 'GET';
    finalBody = method.body !== undefined ? method.body : null;
    if (method.headers) customHeaders = method.headers;
  } else {
    finalMethod = method || 'GET';
    finalBody = body;
  }

  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...customHeaders
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (typeof window !== 'undefined') {
    const isStandalone = window.matchMedia?.('(display-mode: standalone)').matches ||
                         window.navigator?.standalone === true ||
                         document.referrer?.includes('android-app://');
    if (isStandalone) {
      headers['X-PWA-Mode'] = 'standalone';
    }
  }

  const config = {
    method: finalMethod,
    headers
  };

  if (finalBody !== null && finalBody !== undefined) {
    config.body = typeof finalBody === 'string' ? finalBody : JSON.stringify(finalBody);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    const isAuthRoute = endpoint.startsWith('/auth/');
    
    if (response.status === 401 && !isAuthRoute) {
      logout();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('auth_required'));
      }
      throw new Error('Authentication expired. Please log in again.');
    }

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'API Request failed');
    }

    return data;
  } catch (error) {
    console.error(`Fetch error on ${endpoint}:`, error);
    if (!error.message?.includes('Authentication expired')) {
      captureException(error, {
        extra: {
          endpoint,
          method: finalMethod,
          isOnline: isOnline()
        }
      });
    }
    throw error;
  }
}

export const get = (endpoint, options) => request(endpoint, { ...options, method: 'GET' });
export const post = (endpoint, body, options) => request(endpoint, { ...options, method: 'POST', body });
export const put = (endpoint, body, options) => request(endpoint, { ...options, method: 'PUT', body });
export const del = (endpoint, options) => request(endpoint, { ...options, method: 'DELETE' });

